import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isArray from 'lodash/isArray';
import Accordion from '@Pages/ProfileSettings/Preferences/components/Accordion';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { Card } from '@els/els-react--card';
import { Pill } from '@els/els-react--pill';

function ProfileSettingsAccordionItem({
  onClose,
  children,
  onSave,
  label,
  value,
  title,
  className,
  notTagged,
  resetButton,
  onReset = () => {},
  formValid = true,
  validateOnSave = false,
  headingLevel = 'h4',
  dataE2E,
}) {
  const Heading = label ? headingLevel : 'span';
  const qualify = (action) => (label ? `${action} ${label}` : action);
  const [open, setOpen] = useState(false);
  const editButtonRef = useRef(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (wasOpenRef.current && !open) {
      editButtonRef.current?.focus();
    }
    wasOpenRef.current = open;
  }, [open]);

  const toggleOpen = () => {
    setOpen(!open);
    onClose();
  };

  const handleClose = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setOpen(false);
    onClose();
  };

  const handleCancel = (e) => {
    handleClose(e);
  };

  const handleReset = (e) => {
    e.stopPropagation();
    onReset(null, () => setOpen(false));
  };

  const handleSave = (e) => {
    e?.preventDefault();

    if (!validateOnSave) {
      if (!formValid) {
        return;
      }
      onSave();
      setOpen(false);
      return;
    }
    onSave(() => setOpen(false));
  };

  const renderValue = () => {
    if (isArray(value)) {
      return (
        <span className="profile-settings-accordion-row_list">
          {/* eslint-disable-next-line no-shadow */}
          {value.map(({ title, value }, index) =>
            !notTagged && value ? (
              <Pill
                // eslint-disable-next-line react/no-array-index-key
                key={`${title}-${value}_${index}`}
                className="profile-settings-accordion-row_list-tagged"
                pillColor="n1"
                textColor="text">
                {value}
              </Pill>
            ) : (
              <span
                // eslint-disable-next-line react/no-array-index-key
                key={`${title}-${value}_${index}`}>
                {value}
              </span>
            )
          )}
        </span>
      );
    }

    if (!notTagged && value) {
      return (
        <Pill className="profile-settings-accordion-row_value-tagged" pillColor="n1" textColor="text">
          {value}
        </Pill>
      );
    }

    return (
      <span
        className={classnames({
          'profile-settings-accordion-row_value-titled': !value && title,
        })}>
        {value || title}
      </span>
    );
  };

  return (
    <Accordion
      open={open}
      dataE2E={dataE2E}
      className={classnames('profile-settings-accordion-item', className)}
      heading={
        <div data-e2e="profile-settings-accordion-row" className="rx-accordion-row profile-settings-accordion-row">
          <div data-e2e="profile-settings-accordion-row-toggle" className="profile-settings-accordion-row__toggle">
            <div className="profile-settings-accordion-row__text">
              {label ? (
                <Heading className="profile-settings-accordion-row_label u-els-font-size-h4 u-els-margin-none">{label}</Heading>
              ) : null}
              {!open && renderValue()}
            </div>
          </div>
          <div className="profile-settings-accordion-row__actions">
            {resetButton && !open && (
              <div className="profile-settings-accordion-row_reset">
                <ButtonWithIcon
                  type="secondary"
                  data-e2e="profile-settings-accordion-row-reset"
                  sprite="Undo"
                  iconRight
                  onClick={handleReset}
                  aria-label={label ? `Reset to default: ${label}` : undefined}
                  className="e2e-profile-settings-reset">
                  Reset to default
                </ButtonWithIcon>
              </div>
            )}
            <div className="profile-settings-accordion-row_icon">
              {!open && (
                <ButtonWithIcon
                  ref={editButtonRef}
                  type="secondary"
                  linkIconSize="s"
                  data-e2e="profile-settings-accordion-row-edit"
                  sprite="Edit"
                  iconOnly
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOpen();
                  }}>
                  {qualify('Edit')}
                </ButtonWithIcon>
              )}
            </div>
          </div>
        </div>
      }
      body={
        open ? (
          <Card
            className="profile-settings-accordion-row__content-card"
            borderWeight="light"
            radius="m"
            size="small"
            heading={
              <div className="profile-settings-accordion-row__card-heading">
                {/* the closed-state row heading is display:none while open, so this is the only heading */}
                {label ? <Heading className="u-els-font-size-h4 u-els-margin-none">{label}</Heading> : null}
                <ButtonWithIcon
                  type="secondary"
                  linkIconSize="s"
                  sprite="Close"
                  data-e2e="profile-settings-accordion-row-close"
                  iconOnly
                  onClick={handleClose}>
                  {qualify('Close')}
                </ButtonWithIcon>
              </div>
            }>
            <form className="profile-settings-accordion-row__content" noValidate onSubmit={handleSave}>
              {children}
              <div className="profile-settings-accordion-row__content_footer">
                <Button
                  type="borderless"
                  data-e2e="profile-settings-accordion-row-cancel"
                  onClick={handleCancel}
                  aria-label={qualify('Cancel')}
                  className="e2e-profile-settings-cancel">
                  Cancel
                </Button>
                <Button
                  type="primary"
                  data-e2e="profile-settings-accordion-row-save"
                  disabled={!formValid}
                  onClick={handleSave}
                  aria-label={qualify('Save')}
                  className="e2e-profile-settings-save">
                  Save
                </Button>
              </div>
            </form>
          </Card>
        ) : null
      }
    />
  );
}

ProfileSettingsAccordionItem.propTypes = {
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  onSave: PropTypes.func.isRequired,
  formValid: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.any,
  title: PropTypes.string,
  validateOnSave: PropTypes.bool,
  className: PropTypes.string,
  notTagged: PropTypes.bool,
  resetButton: PropTypes.bool,
  onReset: PropTypes.func,
  headingLevel: PropTypes.oneOf(['h2', 'h3', 'h4', 'h5', 'h6']),
  dataE2E: PropTypes.string,
};

export default ProfileSettingsAccordionItem;
